import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  BadgeCheck,
  Banknote,
  CircleDollarSign,
  Coins,
  Eye,
  FilterX,
  Link2,
  MoreHorizontal,
  ReceiptText,
  Search,
} from 'lucide-react'

import { KpiCard, KpiCardSkeleton } from '@/features/dashboard/components/KpiCard'
import { DashboardSectionHeader } from '@/features/dashboard/components/DashboardSectionHeader'
import { formatDashboardDateFr, formatDashboardDateTimeFr } from '@/features/dashboard/utils/semanticBadges'
import { useAuthSession } from '@/features/auth/session'
import { fetchAgents } from '@/features/agents/api'
import { fetchPrograms } from '@/features/programs/api'
import { buildProspectDetailPath } from '@/features/prospects/paths'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { StateSurface, StateSurfaceIconAsset } from '@/components/app/StateSurface'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { IconActionButton } from '@/components/ui/icon-action-button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ApiError } from '@/lib/api'
import { getAppLocale } from '@/lib/locale'
import type { TransactionQueryParams, TransactionRecord, TransactionStatus } from '@/types/transactions'

import { fetchTransactionSummary, fetchTransactions } from '../api'

type TransactionSortKey =
  | 'transaction'
  | 'prospect'
  | 'program'
  | 'status'
  | 'sync'
  | 'amount'
  | 'points'
  | 'occurred'

const TRANSACTIONS_REFRESH_INTERVAL_MS = 15_000

const statusSortOrder: Record<TransactionStatus, number> = {
  detected: 1,
  pending: 1,
  validated: 2,
  rejected: 3,
  paid: 4,
}

function formatCurrency(amount: number, currencyCode: string) {
  return new Intl.NumberFormat(getAppLocale(), {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(amount)
}

function toTimestamp(value: string | null) {
  if (!value) return Number.NEGATIVE_INFINITY
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

function transactionPointsValue(transaction: TransactionRecord) {
  return transaction.status === 'paid'
    ? transaction.points_awarded ?? 0
    : transaction.projected_points ?? 0
}

function transactionPointsLabel(t: (key: string) => string, transaction: TransactionRecord) {
  const isEarned = transaction.status === 'paid'
  const points = isEarned ? transaction.points_awarded : transaction.projected_points

  return {
    label: isEarned ? t('transactions.table.earnedPoints') : t('transactions.table.projectedPoints'),
    value: points === null ? '—' : points.toLocaleString(getAppLocale()),
    className: isEarned
      ? 'text-emerald-700 dark:text-emerald-300'
      : 'text-muted-foreground',
  }
}

function TransactionsPageSkeleton({ isAgentView }: { isAgentView: boolean }) {
  const kpiCount = isAgentView ? 6 : 5
  const gridCols = isAgentView ? 'xl:grid-cols-6' : 'xl:grid-cols-5'

  return (
    <section className="app-section">
      <PageHeader
        title={<Skeleton className="h-6 w-40" />}
        right={
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-9 w-full sm:w-[240px]" />
            <Skeleton className="h-9 w-full sm:w-[160px]" />
            <Skeleton className="h-9 w-full sm:w-[160px]" />
            <Skeleton className="h-9 w-full sm:w-[148px]" />
            <Skeleton className="h-9 w-full sm:w-[148px]" />
          </div>
        }
      />

      <div className={`grid gap-3 sm:grid-cols-2 ${gridCols}`}>
        {Array.from({ length: kpiCount }).map((_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>

      <article className="rounded-lg bg-card p-3 sm:p-4">
        <Skeleton className="mb-4 h-5 w-48" />

        <div className="overflow-hidden rounded-lg bg-background/40">
          <div className="h-11 bg-muted/30" />
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="h-16 border-t border-border/50 bg-card/70 first:border-t-0" />
          ))}
        </div>
      </article>
    </section>
  )
}

export function TransactionsPage() {
  const { t } = useTranslation()
  const { user, hasPermission } = useAuthSession()
  const isAgentView = Boolean(user?.agent_profile)
  const canViewAgents = !isAgentView && hasPermission('agent.view')
  const canViewPrograms = hasPermission('program.view')

  const statusPresentation: Record<TransactionStatus, { label: string; className: string }> = {
    detected: {
      label: t('transactions.status.pending'),
      className:
        'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    },
    pending: {
      label: t('transactions.status.pending'),
      className:
        'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    },
    validated: {
      label: t('transactions.status.validated'),
      className:
        'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
    },
    rejected: {
      label: t('transactions.status.rejected'),
      className:
        'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
    },
    paid: {
      label: t('transactions.status.paid'),
      className:
        'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    },
  }

  const invoiceStatusPresentation: Record<Exclude<TransactionRecord['invoice_status'], null>, { label: string; className: string }> = {
    pending: {
      label: t('transactions.invoiceStatus.pending'),
      className: 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
    },
    paid: {
      label: t('transactions.invoiceStatus.paid'),
      className: 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    },
    unpaid: {
      label: t('transactions.invoiceStatus.unpaid'),
      className: 'border-transparent bg-muted text-muted-foreground',
    },
    overdue: {
      label: t('transactions.invoiceStatus.overdue'),
      className: 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
    },
    cancelled: {
      label: t('transactions.invoiceStatus.cancelled'),
      className: 'border-transparent bg-gray-500/15 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300',
    },
  }

  function transactionSyncPresentation(transaction: TransactionRecord) {
    if (transaction.status === 'rejected') {
      return {
        label: t('transactions.sync.rejected'),
        className:
          'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
        helper: transaction.rejected_at
          ? t('transactions.sync.rejectedAt', { date: formatDashboardDateFr(transaction.rejected_at) })
          : t('transactions.sync.rejected'),
        rank: 0,
      }
    }

    if (transaction.iacrm_transaction_id && transaction.last_synced_at) {
      return {
        label: t('transactions.sync.synced'),
        className:
          'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
        helper: transaction.last_synced_at ? formatDashboardDateFr(transaction.last_synced_at) : '',
        rank: 3,
      }
    }

    if (transaction.last_synced_at) {
      return {
        label: t('transactions.sync.localTrace'),
        className:
          'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
        helper: transaction.last_synced_at ? formatDashboardDateFr(transaction.last_synced_at) : '',
        rank: 2,
      }
    }

    return {
      label: t('transactions.sync.pending'),
      className:
        'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
      helper: t('transactions.sync.noSync'),
      rank: 1,
    }
  }

  function compareTransactions(
    left: TransactionRecord,
    right: TransactionRecord,
    key: TransactionSortKey,
    direction: SortDirection,
  ) {
    const modifier = direction === 'asc' ? 1 : -1
    const result =
      key === 'occurred'
        ? toTimestamp(left.occurred_at) - toTimestamp(right.occurred_at)
        : key === 'points'
          ? transactionPointsValue(left) - transactionPointsValue(right)
          : key === 'amount'
            ? left.amount - right.amount
            : key === 'sync'
              ? transactionSyncPresentation(left).rank - transactionSyncPresentation(right).rank
              : key === 'status'
                ? statusSortOrder[left.status] - statusSortOrder[right.status]
                : key === 'program'
                  ? (left.program_name ?? '').localeCompare(right.program_name ?? '')
                  : key === 'prospect'
                    ? (left.prospect_name ?? left.prospect_company_name ?? '').localeCompare(
                        right.prospect_name ?? right.prospect_company_name ?? '',
                      )
                    : `${left.product_name} ${left.transaction_reference}`.localeCompare(
                        `${right.product_name} ${right.transaction_reference}`,
                      )

    return result * modifier
  }

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | TransactionStatus>('all')
  const [programFilter, setProgramFilter] = useState('all')
  const [agentFilter, setAgentFilter] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState<TransactionSortKey>('occurred')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const hasActiveFilters =
    search !== '' ||
    statusFilter !== 'all' ||
    programFilter !== 'all' ||
    agentFilter !== 'all' ||
    dateFrom !== '' ||
    dateTo !== ''

  const kpiQueryParams = useMemo<TransactionQueryParams>(
    () => ({
      programId: programFilter,
      agentId: canViewAgents ? agentFilter : undefined,
      dateFrom,
      dateTo,
    }),
    [agentFilter, canViewAgents, dateFrom, dateTo, programFilter],
  )

  const tableQueryParams = useMemo<TransactionQueryParams>(
    () => ({
      ...kpiQueryParams,
      search,
      status: statusFilter,
    }),
    [kpiQueryParams, search, statusFilter],
  )

  const transactionsQuery = useQuery({
    queryKey: ['transactions', 'list', tableQueryParams],
    queryFn: () => fetchTransactions(tableQueryParams),
    placeholderData: keepPreviousData,
    refetchInterval: TRANSACTIONS_REFRESH_INTERVAL_MS,
  })

  const summaryQuery = useQuery({
    queryKey: ['transactions', 'summary', kpiQueryParams],
    queryFn: () => fetchTransactionSummary(kpiQueryParams),
    placeholderData: keepPreviousData,
    refetchInterval: TRANSACTIONS_REFRESH_INTERVAL_MS,
  })

  const programsQuery = useQuery({
    queryKey: ['transactions', 'program-options'],
    queryFn: fetchPrograms,
    enabled: canViewPrograms,
  })

  const agentsQuery = useQuery({
    queryKey: ['transactions', 'agent-options'],
    queryFn: fetchAgents,
    enabled: canViewAgents,
  })

  const transactions = transactionsQuery.data?.data ?? []
  const summary = summaryQuery.data?.data
  const statusBreakdown = summary?.status_breakdown

  const programOptions = useMemo(() => {
    if (programsQuery.data?.data) {
      return programsQuery.data.data
        .map((program) => ({ id: program.id, name: program.name }))
        .sort((left, right) => left.name.localeCompare(right.name))
    }

    return Array.from(
      new Map(
        transactions
          .filter((transaction) => transaction.program_id && transaction.program_name)
          .map((transaction) => [
            transaction.program_id,
            { id: transaction.program_id, name: transaction.program_name ?? t('transactions.fallback.program') },
          ]),
      ).values(),
    ).sort((left, right) => left.name.localeCompare(right.name))
  }, [programsQuery.data?.data, transactions, t])

  const agentOptions = useMemo(() => {
    if (agentsQuery.data?.data) {
      return agentsQuery.data.data
        .map((agent) => ({ id: agent.id, name: agent.display_name ?? t('transactions.fallback.agent') }))
        .sort((left, right) => left.name.localeCompare(right.name))
    }

    return Array.from(
      new Map(
        transactions
          .filter((transaction) => transaction.agent_id && transaction.agent_name)
          .map((transaction) => [
            transaction.agent_id,
            { id: transaction.agent_id, name: transaction.agent_name ?? t('transactions.fallback.agent') },
          ]),
      ).values(),
    ).sort((left, right) => left.name.localeCompare(right.name))
  }, [agentsQuery.data?.data, transactions, t])

  const sortedTransactions = useMemo(
    () => [...transactions].sort((left, right) => compareTransactions(left, right, sortKey, sortDirection)),
    [sortDirection, sortKey, transactions],
  )

  function handleSort(nextKey: TransactionSortKey) {
    setPage(1)
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(nextKey)
    setSortDirection(['amount', 'points', 'occurred'].includes(nextKey) ? 'desc' : 'asc')
  }

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter, programFilter, agentFilter, dateFrom, dateTo])

  const totalItems = sortedTransactions.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const safePage = Math.min(page, totalPages)

  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sortedTransactions.slice(start, start + pageSize)
  }, [pageSize, safePage, sortedTransactions])

  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  const isInitialLoading = transactionsQuery.isLoading || summaryQuery.isLoading
  const isKpiLoading = summaryQuery.isFetching && summaryQuery.isPlaceholderData

  if (isInitialLoading) {
    return <TransactionsPageSkeleton isAgentView={isAgentView} />
  }

  if (transactionsQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(transactionsQuery.error as ApiError).message}
      </article>
    )
  }

  if (summaryQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(summaryQuery.error as ApiError).message}
      </article>
    )
  }

  return (
    <section className="app-section">
      <PageHeader
        title={t('transactions.pageTitle')}
        right={
          <PageHeaderToolbar>
            {hasActiveFilters ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        setSearch('')
                        setStatusFilter('all')
                        setProgramFilter('all')
                        setAgentFilter('all')
                        setDateFrom('')
                        setDateTo('')
                      }}
                      aria-label={t('transactions.filters.clearFilters')}
                    >
                      <FilterX className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('transactions.filters.clearFilters')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}

            <div className="relative w-full sm:w-[240px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="transactions-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('transactions.filters.searchPlaceholder')}
                className="pl-9"
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as 'all' | TransactionStatus)}
            >
              <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder={t('transactions.filters.allStatuses')} /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('transactions.filters.statusLabel')}</SelectLabel>
                  <SelectItem value="all">{t('transactions.filters.allStatuses')}</SelectItem>
                  {Object.entries(statusPresentation)
                    .filter(([key]) => key !== 'detected')
                    .map(([key, status]) => (
                      <SelectItem key={key} value={key}>{status.label}</SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {programOptions.length > 1 ? (
              <Select value={programFilter} onValueChange={setProgramFilter}>
                <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder={t('transactions.filters.allPrograms')} /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t('transactions.filters.programLabel')}</SelectLabel>
                    <SelectItem value="all">{t('transactions.filters.allPrograms')}</SelectItem>
                    {programOptions.map((program) => (
                      <SelectItem key={program.id} value={program.id}>{program.name}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : null}

            {canViewAgents && agentOptions.length > 1 ? (
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder={t('transactions.filters.allAgents')} /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t('transactions.filters.agentLabel')}</SelectLabel>
                    <SelectItem value="all">{t('transactions.filters.allAgents')}</SelectItem>
                    {agentOptions.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : null}

            <Input
              id="transactions-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="w-full sm:w-[148px]"
              aria-label={t('transactions.filters.startDate')}
            />
            <Input
              id="transactions-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="w-full sm:w-[148px]"
              aria-label={t('transactions.filters.endDate')}
            />

          </PageHeaderToolbar>
        }
      />

      <div className={`grid gap-3 sm:grid-cols-2 ${isAgentView ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
        {isAgentView ? (
          <>
            <KpiCard
              title={t('transactions.kpi.agent.pending.title')}
              value={((statusBreakdown?.pending ?? 0) + (statusBreakdown?.detected ?? 0)).toLocaleString(getAppLocale())}
              description={t('transactions.kpi.agent.pending.description')}
              icon={Link2}
              tone="info"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.agent.validated.title')}
              value={(statusBreakdown?.validated ?? 0).toLocaleString(getAppLocale())}
              description={t('transactions.kpi.agent.validated.description')}
              icon={BadgeCheck}
              tone="warning"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.agent.paid.title')}
              value={(statusBreakdown?.paid ?? 0).toLocaleString(getAppLocale())}
              description={t('transactions.kpi.agent.paid.description')}
              icon={Banknote}
              tone="success"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.agent.rejected.title')}
              value={(statusBreakdown?.rejected ?? 0).toLocaleString(getAppLocale())}
              description={t('transactions.kpi.agent.rejected.description')}
              icon={CircleDollarSign}
              tone="danger"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.agent.points.title')}
              value={`${(summary?.points_awarded_total ?? 0).toLocaleString(getAppLocale())} pts`}
              description={t('transactions.kpi.agent.points.description')}
              icon={Coins}
              tone="info"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.agent.projectedPoints.title')}
              value={`${(summary?.projected_points_total ?? 0).toLocaleString(getAppLocale())} pts`}
              description={t('transactions.kpi.agent.projectedPoints.description')}
              icon={Coins}
              tone="primary"
              isLoading={isKpiLoading}
            />
          </>
        ) : (
          <>
            <KpiCard
              title={t('transactions.kpi.owner.volume.title')}
              value={formatCurrency(summary?.total_amount ?? 0, 'EUR')}
              description={t('transactions.kpi.owner.volume.description', { count: summary?.transaction_count ?? 0 })}
              icon={CircleDollarSign}
              tone="primary"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.owner.transactions.title')}
              value={(summary?.transaction_count ?? 0).toLocaleString(getAppLocale())}
              description={t('transactions.kpi.owner.transactions.description', { count: summary?.linked_prospect_count ?? 0 })}
              icon={Link2}
              tone="info"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.owner.validated.title')}
              value={formatCurrency(summary?.validated_amount ?? 0, 'EUR')}
              description={t('transactions.kpi.owner.validated.description')}
              icon={BadgeCheck}
              tone="warning"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.owner.paid.title')}
              value={formatCurrency(summary?.paid_amount ?? 0, 'EUR')}
              description={t('transactions.kpi.owner.paid.description')}
              icon={Banknote}
              tone="success"
              isLoading={isKpiLoading}
            />
            <KpiCard
              title={t('transactions.kpi.owner.points.title')}
              value={`${(summary?.points_awarded_total ?? 0).toLocaleString(getAppLocale())} pts`}
              description={t('transactions.kpi.owner.points.description')}
              icon={Coins}
              tone="info"
              isLoading={isKpiLoading}
            />
          </>
        )}
      </div>

      <article className="rounded-lg bg-card p-3 sm:p-4">
        <DashboardSectionHeader
          title={isAgentView ? t('transactions.section.agent') : t('transactions.section.owner')}
        />

        {totalItems === 0 ? (
          <StateSurface
            variant="section"
            size="md"
            asset={<StateSurfaceIconAsset icon={ReceiptText} size="md" />}
            title={t('transactions.empty.title')}
            description={t('transactions.empty.hint')}
            primaryAction={
              hasActiveFilters
                ? {
                    label: t('transactions.filters.clearFilters'),
                    onClick: () => {
                      setSearch('')
                      setStatusFilter('all')
                      setProgramFilter('all')
                      setAgentFilter('all')
                      setDateFrom('')
                      setDateTo('')
                    },
                    size: 'default',
                  }
                : undefined
            }
          />
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    {isAgentView ? (
                      <>
                        <SortableTableHead
                          sortKey="transaction"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="min-w-[13rem]"
                        >
                          {t('transactions.table.transaction')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="prospect"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="hidden min-w-[12rem] md:table-cell"
                        >
                          {t('transactions.table.prospect')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="program"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="hidden min-w-[14rem] lg:table-cell"
                        >
                          {t('transactions.table.program')}
                        </SortableTableHead>
                      </>
                    ) : (
                      <>
                        <SortableTableHead
                          sortKey="transaction"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="min-w-[12rem]"
                        >
                          {t('transactions.table.transaction')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="prospect"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="hidden min-w-[11rem] md:table-cell"
                        >
                          {t('transactions.table.prospect')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="program"
                          activeKey={sortKey}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="hidden min-w-[10rem] lg:table-cell"
                        >
                          {t('transactions.table.program')}
                        </SortableTableHead>
                      </>
                    )}
                    <SortableTableHead
                      sortKey="status"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={handleSort}
                    >
                      {t('transactions.table.status')}
                    </SortableTableHead>
                    {!isAgentView ? (
                      <SortableTableHead
                        sortKey="sync"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="hidden min-w-[9rem] xl:table-cell"
                      >
                        {t('transactions.table.sync')}
                      </SortableTableHead>
                    ) : null}
                    {!isAgentView ? (
                      <SortableTableHead
                        sortKey="amount"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="hidden text-right sm:table-cell"
                        align="right"
                      >
                        {t('transactions.table.amount')}
                      </SortableTableHead>
                    ) : null}
                    <SortableTableHead
                      sortKey="points"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={handleSort}
                      className="hidden text-right xl:table-cell"
                      align="right"
                    >
                      {t('transactions.table.points')}
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="occurred"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={handleSort}
                      className="hidden min-w-[8rem] lg:table-cell"
                    >
                      {t('transactions.table.occurred')}
                    </SortableTableHead>
                    <TableHead className="w-[84px] pe-3 text-right">{t('transactions.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageSlice.map((transaction, index) => {
                    const rank = (safePage - 1) * pageSize + index + 1
                    const sync = transactionSyncPresentation(transaction)
                    const points = transactionPointsLabel(t, transaction)
                    const prospectHref = transaction.prospect
                      ? buildProspectDetailPath({
                          prospectId: transaction.prospect.id,
                        })
                      : null

                    return (
                      <TableRow key={transaction.id} className="hover:bg-muted/20">
                        <TableCell className="text-center text-muted-foreground">{rank}</TableCell>
                        {isAgentView ? (
                          <>
                            <TableCell className="max-w-[14rem]">
                              <Link
                                to={`/transactions/${transaction.id}`}
                                className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              >
                                <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                                  {transaction.product_name ?? transaction.transaction_reference ?? t('transactions.fallback.noProgram')}
                                </p>
                                <p className="truncate font-mono text-[11px] text-muted-foreground">
                                  {transaction.transaction_code ?? t('transactions.codeUnavailable')}
                                </p>
                              </Link>
                            </TableCell>
                            <TableCell className="hidden max-w-[14rem] md:table-cell">
                              {prospectHref ? (
                                <Link
                                  to={prospectHref}
                                  className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                  <div className="flex min-w-0 items-baseline gap-2">
                                    <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                                      {transaction.prospect_name ?? t('transactions.fallback.linkedProspect')}
                                    </p>
                                    {transaction.prospect_company_name ? (
                                      <span className="min-w-0 truncate text-xs text-muted-foreground">
                                        {transaction.prospect_company_name}
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {transaction.prospect_code ?? t('transactions.codeUnavailable')}
                                  </p>
                                </Link>
                              ) : (
                                <div className="px-1 py-1">
                                  <p className="truncate text-sm text-foreground">{t('transactions.fallback.noLinkedProspect')}</p>
                                  <p className="truncate text-[11px] text-muted-foreground">{t('transactions.fallback.unattached')}</p>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="hidden max-w-[15rem] lg:table-cell">
                              {transaction.program_id && transaction.program_name ? (
                                <Link
                                  to={`/programs/${transaction.program_id}`}
                                  className="flex min-w-0 items-center gap-2 rounded-md p-1 text-primary outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                  <Avatar className="size-8 rounded-md">
                                    <AvatarImage
                                      src={transaction.business_logo_url ?? undefined}
                                      alt={transaction.business_name ?? transaction.program_name}
                                      className="object-cover"
                                    />
                                    <AvatarFallback className="rounded-md text-xs font-semibold uppercase">
                                      {(transaction.business_name ?? transaction.program_name ?? '?').slice(0, 1)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="min-w-0">
                                    <span className="block truncate font-medium underline underline-offset-4 decoration-border hover:decoration-primary">
                                      {transaction.program_name}
                                    </span>
                                    <span className="block truncate text-[11px] text-muted-foreground no-underline">
                                      {transaction.program_code ?? t('transactions.codeUnavailable')}
                                    </span>
                                  </span>
                                </Link>
                              ) : (
                                <div className="px-1 py-1 text-sm text-muted-foreground">{t('transactions.fallback.noProgram')}</div>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              <Link
                                to={`/transactions/${transaction.id}`}
                                className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              >
                                <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                                  {transaction.product_name}
                                </p>
                                <p className="truncate font-mono text-[11px] text-muted-foreground">
                                  {transaction.transaction_code ?? t('transactions.codeUnavailable')}
                                </p>
                                <p className="truncate text-[11px] text-muted-foreground/80">
                                  {transaction.transaction_reference}
                                </p>
                                <p className="mt-0.5 truncate text-[11px] text-muted-foreground sm:hidden">
                                  {formatCurrency(transaction.amount, transaction.currency_code)}
                                </p>
                              </Link>
                            </TableCell>
                            <TableCell className="hidden max-w-[12rem] md:table-cell">
                              {prospectHref ? (
                                <Link
                                  to={prospectHref}
                                  className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                  <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                                    {transaction.prospect_name ?? t('transactions.fallback.linkedProspect')}
                                  </p>
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {transaction.prospect_code ?? t('transactions.codeUnavailable')}
                                  </p>
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {transaction.prospect_company_name ?? t('transactions.fallback.noCompany')}
                                  </p>
                                </Link>
                              ) : (
                                <div className="px-1 py-1">
                                  <p className="truncate text-sm text-foreground">{t('transactions.fallback.noLinkedProspect')}</p>
                                  <p className="truncate text-[11px] text-muted-foreground">{t('transactions.fallback.unattached')}</p>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="hidden max-w-[11rem] lg:table-cell">
                              {transaction.program_id && transaction.program_name ? (
                                <Link
                                  to={`/programs/${transaction.program_id}`}
                                  className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                  <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                                    {transaction.program_name}
                                  </p>
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {transaction.program_code ?? t('transactions.codeUnavailable')}
                                  </p>
                                  <p className="truncate text-[11px] text-muted-foreground/80">
                                    {transaction.agent_name ?? t('transactions.fallback.noAgent')}
                                  </p>
                                </Link>
                              ) : (
                                <div className="px-1 py-1 text-sm text-muted-foreground">{t('transactions.fallback.noProgram')}</div>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          {transaction.invoice_status ? (
                            <Badge className={invoiceStatusPresentation[transaction.invoice_status].className}>
                              {invoiceStatusPresentation[transaction.invoice_status].label}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        {!isAgentView ? (
                          <TableCell className="hidden xl:table-cell">
                            <div className="flex flex-col gap-1">
                              <Badge className={sync.className}>{sync.label}</Badge>
                              <span className="text-[11px] text-muted-foreground">{sync.helper}</span>
                            </div>
                          </TableCell>
                        ) : null}
                        {!isAgentView ? (
                          <TableCell className="hidden text-right font-medium tabular-nums sm:table-cell">
                            {formatCurrency(transaction.amount, transaction.currency_code)}
                          </TableCell>
                        ) : null}
                        <TableCell className="hidden text-right tabular-nums xl:table-cell">
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-sm font-semibold tabular-nums ${points.className}`}>
                              {points.value} pts
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {points.label}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden text-muted-foreground lg:table-cell">
                          {formatDashboardDateTimeFr(transaction.occurred_at)}
                        </TableCell>
                        <TableCell className="pe-3 text-right">

                            <div className="flex justify-end gap-2">
                              <IconActionButton
                                icon={<Eye className="size-4" />}
                                tooltip={t('transactions.actions.view')}
                                to={`/transactions/${transaction.id}`}
                                className="hidden md:inline-flex"
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    className="md:hidden"
                                    aria-label={t('transactions.actions.moreOptions')}
                                  >
                                    <MoreHorizontal className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem asChild>
                                    <Link to={`/transactions/${transaction.id}`}>
                                      <Eye className="size-4 text-primary" />
                                      <span>{t('transactions.actions.view')}</span>
                                    </Link>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>

                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <TablePaginationBar
              page={safePage}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        )}
      </article>
    </section>
  )
}
